import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Button, Row, Col, Table, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label } from "reactstrap";
import Swal from "sweetalert2";
import Header from "../../../layout/Header/Header";
import { api, mensajeError, hoy, dinero, numero, nombreUsuario, Estado } from "./gastosFlujoUtil";

// Catálogo de insumos de gastos (cajas, playo, cinta...) con su existencia.
// La existencia sube con los gastos pagados de una orden y baja con las salidas.
const VACIO = { nombre: "", unidad: "Pieza", descripcion: "", costo: 0, observaciones: "" };

function InsumosGastos() {
  const { user } = useContext(AuthContext);
  const [insumos, setInsumos] = useState([]);
  const [buscar, setBuscar] = useState("");
  const [verInactivos, setVerInactivos] = useState(false);
  const [form, setForm] = useState(null); // {..campos, _id?}
  const [salida, setSalida] = useState(null); // { insumo, cantidad, fecha, observaciones }
  const [kardex, setKardex] = useState(null); // { insumo, movimientos }
  const [guardando, setGuardando] = useState(false);

  const cargar = () =>
    api.get("insumosGastos").then(setInsumos).catch((e) => Swal.fire({ icon: "error", title: "No se pudieron cargar los insumos", text: mensajeError(e) }));

  useEffect(() => {
    cargar();
  }, []);

  const lista = useMemo(() => {
    const t = buscar.trim().toLowerCase();
    return insumos
      .filter((i) => verInactivos || i.is_active !== "No")
      .filter((i) => !t || `${i.nombre} ${i.descripcion} ${i.unidad}`.toLowerCase().includes(t));
  }, [insumos, buscar, verInactivos]);

  async function guardar() {
    if (!form.nombre.trim()) return Swal.fire({ icon: "warning", title: "Falta el nombre" });
    setGuardando(true);
    try {
      const body = { nombre: form.nombre, unidad: form.unidad, descripcion: form.descripcion, costo: Number(form.costo) || 0, observaciones: form.observaciones };
      if (form._id) await api.patch(`insumosGastos/${form._id}`, body);
      else await api.post("insumosGastos", body);
      setForm(null);
      await cargar();
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se guardó", text: mensajeError(e) });
    }
    setGuardando(false);
  }

  async function cambiarActivo(i) {
    try {
      await api.patch(`insumosGastos/${i._id}`, { is_active: i.is_active === "No" ? "Si" : "No" });
      cargar();
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se pudo cambiar", text: mensajeError(e) });
    }
  }

  async function registrarSalida() {
    const cant = Number(salida.cantidad);
    if (!(cant > 0)) return Swal.fire({ icon: "warning", title: "Captura una cantidad mayor a 0" });
    setGuardando(true);
    try {
      await api.post("movimientosInsumosSalida", { insumos: salida.insumo._id, cantidad: cant, fecha: salida.fecha, observaciones: salida.observaciones || "NA" });
      setSalida(null);
      await cargar();
      Swal.fire({ icon: "success", title: "Salida registrada", timer: 1200, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se registró la salida", text: mensajeError(e) });
    }
    setGuardando(false);
  }

  async function verKardex(insumo) {
    try {
      const movimientos = await api.get(`movimientosInsumos/${insumo._id}`);
      setKardex({ insumo, movimientos });
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se pudo cargar el kárdex", text: mensajeError(e) });
    }
  }

  async function cancelarSalida(m) {
    const ok = await Swal.fire({ icon: "warning", title: "¿Cancelar esta salida?", text: `Regresan ${m.cantidad} a la existencia.`, showCancelButton: true, confirmButtonText: "Sí, cancelar", cancelButtonText: "No" });
    if (!ok.isConfirmed && !ok.value) return;
    try {
      await api.patch(`movimientosInsumosCancelar/${m._id}`);
      await cargar();
      verKardex(kardex.insumo);
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se canceló", text: mensajeError(e) });
    }
  }

  // Kárdex con saldo corrido (solo movimientos activos)
  const filasKardex = useMemo(() => {
    if (!kardex) return [];
    let saldo = 0;
    return kardex.movimientos.map((m) => {
      const activo = m.is_active !== "No";
      if (activo) saldo += m.tipo === "Entrada" ? m.cantidad : -m.cantidad;
      return { ...m, activo, saldo };
    });
  }, [kardex]);

  if (!user.menu_gastos) return <Header />;

  return (
    <>
      <Header />
      <div className="card col-12" style={{ padding: "18px 22px" }}>
        <Row className="align-items-center">
          <Col md={6}>
            <h3 style={{ margin: 0 }}>Insumos de gastos</h3>
            <small style={{ color: "#64748b" }}>Existencias: entran con los gastos pagados de una orden y salen con las salidas.</small>
          </Col>
          <Col md={6} style={{ textAlign: "right" }}>
            {user.gastos_create ? (
              <Button size="sm" color="success" onClick={() => setForm({ ...VACIO })}>
                <i className="fas fa-plus"></i> Nuevo insumo
              </Button>
            ) : null}{" "}
            <Button size="sm" color="danger" href="/MenuGastos">Regresar</Button>
          </Col>
        </Row>
        <Row style={{ margin: "14px 0 8px" }}>
          <Col md={4}>
            <Input bsSize="sm" placeholder="Buscar insumo..." value={buscar} onChange={(e) => setBuscar(e.target.value)} />
          </Col>
          <Col md={4}>
            <Label check style={{ fontSize: "0.8rem", marginLeft: 20 }}>
              <Input type="checkbox" checked={verInactivos} onChange={(e) => setVerInactivos(e.target.checked)} /> Ver inactivos
            </Label>
          </Col>
        </Row>
        <Table size="sm" striped borderless className="table-responsive-xl">
          <thead>
            <tr>
              <th>Insumo</th>
              <th>Unidad</th>
              <th>Descripción</th>
              <th style={{ textAlign: "right" }}>Último costo</th>
              <th style={{ textAlign: "right" }}>Existencia</th>
              <th style={{ textAlign: "right" }}>Valor</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {lista.map((i) => (
              <tr key={i._id} style={{ opacity: i.is_active === "No" ? 0.5 : 1 }}>
                <td><b>{i.nombre}</b></td>
                <td>{i.unidad}</td>
                <td>{i.descripcion}</td>
                <td style={{ textAlign: "right" }}>{dinero(i.costo)}</td>
                <td style={{ textAlign: "right", fontWeight: 700 }}>{numero(i.existencia)}</td>
                <td style={{ textAlign: "right" }}>{dinero((i.existencia || 0) * (i.costo || 0))}</td>
                <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                  <Button size="sm" color="info" title="Kárdex" onClick={() => verKardex(i)}><i className="fas fa-list"></i></Button>{" "}
                  {user.gastos_create ? (
                    <>
                      <Button size="sm" color="warning" title="Registrar salida" disabled={!(i.existencia > 0)} onClick={() => setSalida({ insumo: i, cantidad: "", fecha: hoy(), observaciones: "" })}>
                        <i className="fas fa-sign-out-alt"></i>
                      </Button>{" "}
                      <Button size="sm" color="primary" title="Editar" onClick={() => setForm({ ...i })}><i className="fas fa-edit"></i></Button>{" "}
                      <Button size="sm" color={i.is_active === "No" ? "success" : "secondary"} title={i.is_active === "No" ? "Activar" : "Desactivar"} onClick={() => cambiarActivo(i)}>
                        <i className={i.is_active === "No" ? "fas fa-check" : "fas fa-ban"}></i>
                      </Button>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
            {!lista.length ? (
              <tr><td colSpan={7} style={{ color: "#94a3b8", textAlign: "center" }}>Sin insumos. Da de alta el primero con "Nuevo insumo".</td></tr>
            ) : null}
          </tbody>
        </Table>
      </div>

      {/* Alta / edición */}
      <Modal isOpen={!!form} toggle={() => setForm(null)}>
        <ModalHeader toggle={() => setForm(null)}>{form && form._id ? "Editar insumo" : "Nuevo insumo"}</ModalHeader>
        <ModalBody>
          {form ? (
            <>
              <Label>Nombre *</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej. Caja 40x30x30" />
              <Row style={{ marginTop: 8 }}>
                <Col>
                  <Label>Unidad</Label>
                  <Input type="select" value={form.unidad} onChange={(e) => setForm({ ...form, unidad: e.target.value })}>
                    {["Pieza", "Caja", "Rollo", "Paquete", "Kilo", "Metro", "Litro", "Servicio"].map((u) => <option key={u}>{u}</option>)}
                  </Input>
                </Col>
                <Col>
                  <Label>Costo de referencia</Label>
                  <Input type="number" min="0" step="0.01" value={form.costo} onChange={(e) => setForm({ ...form, costo: e.target.value })} />
                </Col>
              </Row>
              <Label style={{ marginTop: 8 }}>Descripción</Label>
              <Input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
              <Label style={{ marginTop: 8 }}>Observaciones</Label>
              <Input value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />
              {form._id ? <small style={{ color: "#64748b" }}>La existencia no se edita aquí: se mueve con gastos y salidas.</small> : null}
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={guardar} disabled={guardando}>{guardando ? "Guardando..." : "Guardar"}</Button>
          <Button color="secondary" onClick={() => setForm(null)}>Cerrar</Button>
        </ModalFooter>
      </Modal>

      {/* Salida */}
      <Modal isOpen={!!salida} toggle={() => setSalida(null)}>
        <ModalHeader toggle={() => setSalida(null)}>Salida de insumo</ModalHeader>
        <ModalBody>
          {salida ? (
            <>
              <p style={{ marginBottom: 8 }}>
                <b>{salida.insumo.nombre}</b> — existencia: <b>{numero(salida.insumo.existencia)}</b> {salida.insumo.unidad}
              </p>
              <Row>
                <Col>
                  <Label>Cantidad *</Label>
                  <Input type="number" min="0" step="any" value={salida.cantidad} onChange={(e) => setSalida({ ...salida, cantidad: e.target.value })} />
                </Col>
                <Col>
                  <Label>Fecha</Label>
                  <Input type="date" value={salida.fecha} onChange={(e) => setSalida({ ...salida, fecha: e.target.value })} />
                </Col>
              </Row>
              <Label style={{ marginTop: 8 }}>Para qué / quién (observaciones)</Label>
              <Input value={salida.observaciones} onChange={(e) => setSalida({ ...salida, observaciones: e.target.value })} placeholder="Ej. Empaque de pedidos" />
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="warning" onClick={registrarSalida} disabled={guardando}>{guardando ? "Registrando..." : "Registrar salida"}</Button>
          <Button color="secondary" onClick={() => setSalida(null)}>Cerrar</Button>
        </ModalFooter>
      </Modal>

      {/* Kárdex */}
      <Modal size="lg" isOpen={!!kardex} toggle={() => setKardex(null)}>
        <ModalHeader toggle={() => setKardex(null)}>Kárdex — {kardex ? kardex.insumo.nombre : ""}</ModalHeader>
        <ModalBody>
          <div style={{ maxHeight: 420, overflowY: "auto" }}>
            <Table size="sm" bordered>
              <thead>
                <tr><th>Fecha</th><th>Tipo</th><th>Origen</th><th style={{ textAlign: "right" }}>Cantidad</th><th style={{ textAlign: "right" }}>Costo</th><th style={{ textAlign: "right" }}>Existencia</th><th></th></tr>
              </thead>
              <tbody>
                {filasKardex.map((m) => (
                  <tr key={m._id} style={{ opacity: m.activo ? 1 : 0.45, textDecoration: m.activo ? "none" : "line-through" }}>
                    <td>{m.fecha}</td>
                    <td><Estado valor={m.tipo} /></td>
                    <td style={{ fontSize: "0.78rem" }}>
                      {m.tipo === "Entrada"
                        ? `Orden ${m.ordenesGastos && m.ordenesGastos[0] ? m.ordenesGastos[0].idOrdenGastos : ""} · Gasto ${m.gastosGeneral && m.gastosGeneral[0] ? m.gastosGeneral[0].idGastoGeneral : ""} · ${m.proveedoresGastos && m.proveedoresGastos[0] ? m.proveedoresGastos[0].nombre : ""}`
                        : `${m.observaciones || ""} · ${nombreUsuario(m.user)}`}
                    </td>
                    <td style={{ textAlign: "right" }}>{m.tipo === "Entrada" ? "+" : "-"}{numero(m.cantidad)}</td>
                    <td style={{ textAlign: "right" }}>{dinero(m.costoUnitario)}</td>
                    <td style={{ textAlign: "right", fontWeight: 700 }}>{m.activo ? numero(m.saldo) : ""}</td>
                    <td>
                      {m.activo && m.tipo === "Salida" && user.gastos_create ? (
                        <Button size="sm" color="danger" title="Cancelar salida" onClick={() => cancelarSalida(m)}><i className="fas fa-undo"></i></Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
                {!filasKardex.length ? <tr><td colSpan={7} style={{ color: "#94a3b8", textAlign: "center" }}>Sin movimientos.</td></tr> : null}
              </tbody>
            </Table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setKardex(null)}>Cerrar</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default InsumosGastos;
